import cn from "classnames";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";

import { useEffect, useMemo, useRef, useState } from "react";

import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import FlightClassOutlinedIcon from "@mui/icons-material/FlightClassOutlined";
import FlightLandOutlinedIcon from "@mui/icons-material/FlightLandOutlined";
import FlightTakeoffOutlinedIcon from "@mui/icons-material/FlightTakeoffOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Chip,
  Fab,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  Typography,
} from "@mui/material";

import AlertModal, { AlertModalRefProps } from "@/components/AlertModal";
import CustomCodeModal from "@/components/CustomCodeModal";
import ProChip from "@/components/ProChip";
import SearchCheckboxPrograms from "@/components/SearchBar/SearchCheckboxPrograms";
import SearchRangeDate from "@/components/SearchBar/SearchRangeDate";
import SearchSelect from "@/components/SearchBar/SearchSelect";
import SearchSwitchPanorama from "@/components/SearchBar/SearchSwitchPanorama";
import SelectPassengerV2 from "@/components/SelectPassengerV2";
import TrackPriceCardPure from "@/components/TrackPriceCardPure";
import { AlertTypeEnum, cabinOptionMap, passengerTypeOptions } from "@/constants";
import { MAX_ROUTE_CITY_COUNT } from "@/constants/config";
import { DATA_FAST_FORWARD } from "@/constants/eventName";
import {
  FREE_PANORAMA_RANGE,
  FREE_PANORAMA_SEARCH_DAYS,
  MAX_PANORAMA_SEARCH_DAYS,
} from "@/constants/membership";
import { MEMBERSHIP_CONFIG } from "@/constants/membership";
import { searchProgramsOptions } from "@/constants/programs";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import { dispatchEvent, useListenEvent } from "@/hooks/useEventEmitter";
import usePathname from "@/hooks/usePathname";
import usePlatform from "@/hooks/usePlatform";
import useCondition, { ConditionType } from "@/pages/Routes/hooks/useCondition";

import SelectLocation from "../SelectLocation";

interface SearchBarProps {
  isLoading?: boolean;
  onSearch?: (data: ConditionType, isClick?: boolean) => void;
  onChange?: (data: ConditionType) => void;
  searchBtn?: boolean;
  useWhere?: "home" | "routes";
}

type RangeDate = [Dayjs | null, Dayjs | null];

const SearchBar = ({
  onChange,
  onSearch,
  isLoading = false,
  searchBtn = true,
  useWhere = "routes",
}: SearchBarProps) => {
  const { isMobile } = usePlatform();
  const { condition, setCondition, onLocationSwapChange, onDateChange } = useCondition(useWhere);
  const store = useGlobalStoreContext();

  const route_range = store?.persistStore?.route_range || MEMBERSHIP_CONFIG["routes_range"][0];

  const show_upsell = store?.persistStore?.show_upsell || false;
  const route_calendar_range =
    store?.persistStore?.route_calendar_range || MEMBERSHIP_CONFIG["route_calendar_range"][0];
  const disableSwap = useMemo(() => {
    return !condition.fromLocation.length || !condition.toLocation.length;
  }, [condition.fromLocation, condition.toLocation]);

  const datePickerRef = useRef<{ handleChange: (v: RangeDate) => void }>(null);

  const isFastForwardRef = useRef(false);
  const searchBtnRef = useRef();
  const isMainPage = usePathname().isMainPage;

  const [trackPriceOpen, setTrackPriceOpen] = useState(false);
  const modalRef = useRef<AlertModalRefProps>(null);

  useEffect(() => {
    onChange && onChange(condition);
  }, [condition, onChange]);

  useListenEvent(DATA_FAST_FORWARD, (v: RangeDate) => {
    datePickerRef.current?.handleChange(v);
    isFastForwardRef.current = true;
  });

  useEffect(() => {
    if (isFastForwardRef.current) {
      isFastForwardRef.current = false;
      onSearch && onSearch(condition, true);
    }
  }, [condition, onSearch]);

  const SearchBtn = useMemo(() => {
    return !isMobile ? (
      <div className="flex items-center justify-end mt-[15px] ml-[10px] absolute right-0">
        <LoadingButton
          color="primary"
          loading={isLoading}
          variant="contained"
          className="!min-w-[48px] !h-[56px] !w-[56px]"
          onClick={() => onSearch && onSearch(condition, true)}
        >
          <SearchIcon sx={{ fontSize: "24px" }} />
        </LoadingButton>
      </div>
    ) : null;
  }, [isLoading, isMobile, onSearch, condition]);

  const resetBtn = useMemo(() => {
    return (
      <Button
        variant="text"
        size="small"
        color="primary"
        onClick={() => {
          setCondition({
            ...condition,
            fromLocation: [],
            toLocation: [],
            pax: 1,
            children: 0,
            cabins: "Economy&Premium Economy&Business&First",
            programs: [],
            startDate: 0,
            endDate: 0,
          });
        }}
        className={cn("hover:bg-transparent !py-1 !leading-[12px] !text-[12px]")}
      >
        <HighlightOffIcon sx={{ fontSize: 15, color: "primary.main", mr: 0.3 }} />
        Reset
      </Button>
    );
  }, [condition, setCondition]);

  useEffect(() => {
    dispatchEvent("routes_flight_cabins", condition.cabins);
  }, [condition]);

  return (
    <div>
      <div
        className={
          "relative flex justify-between w-full overflow-hidden " +
          (useWhere === "routes" ? "mb-2 mt-[-10px]" : "py-3")
        }
      >
        {/* <SelectPassengerV2
            min={passengerTypeOptions[0].value}
            max={passengerTypeOptions[passengerTypeOptions.length - 1].value}
            value={condition.pax}
            onChange={(data) => {
              setCondition({ ...condition, pax: data });
            }}
            childValue={condition.children}
            onChangeChild={(data) => {
              setCondition({ ...condition, children: data });
            }}
          /> */}

        <div className="flex items-center">
          <SearchSelect
            value={condition.cabins}
            options={cabinOptionMap}
            key={condition.cabins}
            Icon={isMobile ? null : FlightClassOutlinedIcon}
            onChange={(data) => {
              setCondition({ ...condition, cabins: data });

              // dispatchEvent("routes_flight_cabins", data);
            }}
          />

          <SearchCheckboxPrograms
            value={condition.programs}
            Icon={isMobile ? null : CardMembershipIcon}
            options={searchProgramsOptions.map((item) => ({
              value: item.value,
              label: item.fullname,
            }))}
            onChange={(data) => {
              setCondition({ ...condition, programs: data });
            }}
          />
        </div>

        {!isMobile ? (
          <TrackPriceCardPure
            value={trackPriceOpen}
            onChange={(value) => {
              setTrackPriceOpen(value);
              if (value) {
                modalRef.current && modalRef.current.show(AlertTypeEnum.Panorama, {});
              }
            }}
          />
        ) : null}

        {/* {!isMobile ? (
          <div className="flex justify-end flex-1 items-center">
            <FormControlLabel
              control={
                <Switch
                  onChange={(e) => {
                    const newData = {
                      ...condition,
                      searchMode: String(e.target.checked),
                    };
                    setCondition(newData);

                    localStorage.setItem("show_individual_flights", String(e.target.checked));

                    onSearch && onSearch(newData, true);
                  }}
                  checked={condition.searchMode === "true" ? true : false}
                />
              }
              sx={{ color: (theme) => theme.palette.text.secondary }}
              label={
                <Typography className="flex items-center" fontWeight={800} color={"primary"}>
                  {"show individual flights"}
                  <FlightTakeoffOutlinedIcon color="primary" className="ml-[6px]" />
                </Typography>
              }
            />
            <SearchSwitchPanorama
              showUpsell={store?.show_upsell}
              value={false}
              // onChange={onSwitchChange}
            />
          </div>
        ) : null} */}
      </div>
      <Grid
        container
        rowSpacing={2}
        columnSpacing={1}
        className="relative"
        sx={{ paddingRight: !isMobile && searchBtn ? "64px" : 0 }}
      >
        <Grid item xs={12} sm={8.2}>
          <Grid container alignItems="center" rowGap={2}>
            <Grid item xs={12} sm={5.6} className="relative">
              <SelectLocation
                maxCount={MAX_ROUTE_CITY_COUNT}
                placeholder="Where from?"
                Icon={<FlightTakeoffOutlinedIcon />}
                selectData={condition.fromLocation}
                onChange={(data) => {
                  setCondition({ ...condition, fromLocation: data });
                }}
              />
            </Grid>

            {!isMobile ? (
              <Grid item xs={1} sm={0.8} alignItems="center">
                <div className="flex items-center justify-center">
                  <IconButton
                    aria-label="swap"
                    size="small"
                    onClick={onLocationSwapChange}
                    disabled={disableSwap}
                  >
                    <SwapHorizIcon />
                  </IconButton>
                </div>
              </Grid>
            ) : (
              <div className={cn("absolute right-6 z-10", { hidden: disableSwap })}>
                <Fab color="primary" aria-label="swap" size="small" onClick={onLocationSwapChange}>
                  <SwapHorizIcon className="rotate-90" />
                </Fab>
              </div>
            )}

            <Grid item xs={12} sm={5.6}>
              <SelectLocation
                maxCount={MAX_ROUTE_CITY_COUNT}
                placeholder="Where to?"
                Icon={<FlightLandOutlinedIcon />}
                selectData={condition.toLocation}
                onChange={(data) => {
                  setCondition({ ...condition, toLocation: data });
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} sm={3.8}>
          <SearchRangeDate
            ref={datePickerRef}
            label="Date Range"
            defaultValue={[
              condition.startDate ? dayjs(condition.startDate) : null,
              condition.endDate ? dayjs(condition.endDate) : null,
            ]}
            customToolbar={
              show_upsell ? (
                <>
                  <Typography style={{ fontSize: isMobile ? "15px" : "16px" }}>
                    {"Free users can search up to " +
                      FREE_PANORAMA_RANGE +
                      " days with " +
                      FREE_PANORAMA_SEARCH_DAYS +
                      "-day range."}
                  </Typography>
                  <Typography
                    style={{ fontSize: isMobile ? "15px" : "16px" }}
                    fontWeight={900}
                    color="primary"
                  >
                    Upgrade to
                    <ProChip hasRightMargin />
                    {"to search 365 days with " + MAX_PANORAMA_SEARCH_DAYS + "-day range."}
                  </Typography>
                </>
              ) : (
                <Typography style={{ fontSize: isMobile ? "15px" : "16px" }} color="primary">
                  {"Enjoy searching 365 days with " + MAX_PANORAMA_SEARCH_DAYS + "-day range."}
                </Typography>
              )
            }
            onChange={onDateChange}
            maxRangeDays={route_range}
            calendarRangeDays={route_calendar_range}
          />
        </Grid>

        {searchBtn && SearchBtn}
      </Grid>

      <div
        className={classNames(
          "ml-[1px]",
          isMainPage ? "mb-[-19px] mt-[6px]" : "mb-[-10px] mt-[5px]"
        )}
      >
        <div className="flex items-center justify-between">
          <CustomCodeModal />
          {resetBtn}
        </div>
      </div>

      {/* {isMobile ? (
        <div>
          <FormControlLabel
            control={
              <Switch
                onChange={(e) => {
                  const newData = {
                    ...condition,
                    searchMode: String(e.target.checked),
                  };
                  setCondition(newData);

                  localStorage.setItem("show_individual_flights", String(e.target.checked));

                  onSearch && onSearch(newData, true);
                }}
                checked={condition.searchMode === "true" ? true : false}
              />
            }
            sx={{ color: (theme) => theme.palette.text.secondary }}
            label={
              <Typography className="flex items-center">
                {"show individual flights"}{" "}
                <FlightTakeoffOutlinedIcon color="primary" className="ml-[6px]" />
              </Typography>
            }
          />
          <SearchSwitchPanorama showUpsell={store?.show_upsell} value={false} />
        </div>
      ) : null} */}
      {isMobile && searchBtn ? (
        <div className="pb-[18px] pt-[10px]">
          <LoadingButton
            variant="contained"
            className="w-full"
            loading={isLoading}
            startIcon={<SearchIcon />}
            onClick={() => onSearch && onSearch(condition, true)}
          >
            Search
          </LoadingButton>
        </div>
      ) : null}

      <AlertModal ref={modalRef} onReset={() => setTrackPriceOpen(false)} />
    </div>
  );
};

export default SearchBar;
